import React, { useState, useRef, useEffect } from 'react';
import HeaderRow from './HeaderRow';
import DataRows from './DataRows';
import Pagination from './pagination/Pagination';
import ColumnFilter from './columnFilter/ColumnFilter';
import PropTypes from 'prop-types';
import ScrollableArea from '../scrollbar/ScrollableArea';
import { border } from '../styles';
import AsButton from '../AsButton';

const gridContainer = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: '30px 1fr 30px 10px',
    gridGap: 1,
    padding: 0,
    height: '100%'
};
const gridItem = {
    padding: 0
};
const gridItemTable = {
    borderCollapse: 'seperate',
    width: '100%',
    boxShadow: '3px 2px 6px 0 rgba(139,104,70,0.33)'
};
const paginationContainer = {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end'
};
const buttonContainerStyle = {
    width: 332,
    height: 51,
    position: 'absolute',
    top: 781,
    borderRadius: 5,
    backgroundColor: '#FFFFFF',
    boxShadow: '3px 2px 11px 0 rgba(0,0,0,0.33)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly'
};
const buttonStyle = {
    width: 130,
    height: 34,
    fontSize: 15
};
/*
 * Passing a maxHeight will enable the vertical scrollbar, if it is smaller the the size of the content within the container.
 * Not passing onNextClick & onPrevClick will hide the paginators.
 * onKeyPress is for cells of type 'input', it returns the updated data for that row.
 */
const AsGrid = ({
    columnDefs,
    primaryColumn,
    headerRowHeight = 40,
    data,
    newlyCreated,
    pageInfo,
    showColumnFilter = true,
    onPageSizeChange,
    onNextClick,
    onPrevClick,
    onSortOrderChange,
    onActionClick,
    onFavouriteClick,
    onPrimaryColumnClick,
    onColumnFilterChange,
    onActionIconLoad,
    onActionToolTip,
    onCustomClick,
    onKeyPress,
    onScroll,
    onMultiRowDelete,
    style,
    gridContainerStyle,
    dateFormat = 'MMM Do YYYY',
    timeFormat = 'HH:mm',
    locale = 'en-GB',
    currency = 'GBP',
    onTick,
    onTickAll,
    areAllTicked,
    rowHeight = 32,
    actionIconSize = 21,
    emptyRowHeight = rowHeight,
    maxVisibleRows = 15,
    showApplyButton = false,
    onFilterClose,
    scrollableAreaStyle,
    highlightOnClick = false,
    onCustomCellStyle,
    ...props
}) => {
    const [scrollPxH, setScrollPxH] = useState(0);
    const [scrollPxV, setScrollPxV] = useState(0);
    const [tableWidth, setTableWidth] = useState(0);
    const [tableHeight, setTableHeight] = useState(0);
    const [tableOffsetH, setTableOffsetH] = useState(0);
    const [tableOffsetV, setTableOffsetV] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [selectedDeleteRows, setSelectedDeleteRows] = useState([]);
    const [showDeleteBtns, setShowDeleteBtns] = useState(false);

    const tableRef = useRef(null);
    const containerRef = useRef(null);

    const updateRefSizes = () => {
        if (tableRef?.current?.scrollWidth <= containerWidth + 3 && scrollPxH !== 0) {
            setScrollPxH(0);
        }
        setTableHeight((tableRef?.current?.scrollHeight || tableHeight) - 1);
        setTableWidth(tableRef?.current?.scrollWidth);
        setTableOffsetH(tableRef?.current?.offsetLeft);
        setTableOffsetV(tableRef?.current?.offsetTop);
        setContainerWidth(containerRef?.current?.clientWidth);
    };

    useEffect(() => {
        updateRefSizes();
        setSelectedDeleteRows([]);
        setShowDeleteBtns(false);
    }, [tableRef?.current?.scrollWidth, columnDefs, data]);

    const windowResized = () => {
        updateRefSizes();
    };

    useEffect(() => {
        window.addEventListener('resize', windowResized);
        return () => {
            window.removeEventListener('resize', windowResized);
        };
    }, []);

    useEffect(() => {
        if (onScroll) onScroll();
    }, [scrollPxH, scrollPxV]);

    const gridHeight =
        rowHeight * maxVisibleRows + headerRowHeight + (tableWidth > containerWidth ? 20 : 0);

    // utility functions
    const columnDefsToDisplayInOrder = () =>
        columnDefs
            .filter((c) => c.type !== 'hidden' && c.showInList)
            .sort((a, b) => a.displayOrder - b.displayOrder);

    const rowDefinitions = () =>
        columnDefsToDisplayInOrder().map((c) => {
            return {
                type: c.type,
                accessor: c.field,
                accessorDisabled: c.fieldDisabled,
                customType: c.customType,
                isStatus: c.isStatus,
                isSticky: c.isSticky,
                style: c.style,
                onClick: c.onClick,
                buttonText: c.buttonText,
                component: c.component,
                headerComponent: c.headerComponent,
                disabled: c.disabled,
                reverseElements: c.reverseElements,
                tickboxGap: c.gap,
                tickboxSize: c.size,
                tickboxStyle: c.tickboxStyle,
                tickboxContainerStyle: c.tickboxContainerStyle,
                tickboxField: c.tickboxField
            };
        });

    // handlers
    const handleSortOrderChange = (column) => {
        switch (column.sortOrder) {
            case 'asc':
                column.sortOrder = 'desc';
                break;
            default:
                column.sortOrder = 'asc';
                break;
        }

        onSortOrderChange(column);
    };

    const handleColumnFilterChange = (field) => {
        onColumnFilterChange(field);
    };

    const rowIds = columnDefs.filter((c) => c.type === 'rowId').map((c) => c.field);

    const showPagination = typeof onNextClick === 'function' && typeof onPrevClick === 'function';

    const getStatusColumn = () => {
        const definitions = rowDefinitions();
        const columns = definitions.filter((d) => d.isStatus === true);
        if (columns.length > 1) throw new Error('Status column defined more than once');

        return columns[0] ?? {};
    };

    const handleAddToSelectedDeleteRows = (e, record) => {
        if (e.ctrlKey || (e.shiftKey && selectedDeleteRows?.length === 0)) {
            selectedDeleteRows.push(record);
            setSelectedDeleteRows(selectedDeleteRows);
        } else if (e.shiftKey) {
            const firstShiftRow = selectedDeleteRows[selectedDeleteRows?.length - 1];
            let startSelection = false;
            let lastSelection = false;
            const selectedRows = [...selectedDeleteRows];

            const statusColumn = getStatusColumn();

            for (let i = 0; i < data?.length; i++) {
                const d = data[i];
                if (d?.id === record?.id || d?.id === firstShiftRow?.id) {
                    if (startSelection) lastSelection = true;
                    startSelection = !startSelection;
                }

                if (startSelection || lastSelection) {
                    const isDisabled = onActionIconLoad('delete', d[statusColumn?.accessor], d);

                    lastSelection = false;
                    if (!isDisabled) {
                        const filteredData = selectedRows.filter((dr) => dr?.id === d?.id);
                        if (filteredData?.length === 0) {
                            selectedRows.push(d);
                        }
                    }
                }
            }
            setSelectedDeleteRows(selectedRows);
        }
        if (selectedDeleteRows?.length > 0 !== showDeleteBtns)
            setShowDeleteBtns(selectedDeleteRows?.length > 0);
    };

    const handleClearSelectedDeletedRows = () => {
        setSelectedDeleteRows([]);
        setShowDeleteBtns(false);
    };

    const handleMultiRowDelete = () => {
        if (selectedDeleteRows?.length > 0) {
            onMultiRowDelete(selectedDeleteRows);
        }
    };

    return (
        <div style={{ ...gridContainer, ...gridContainerStyle }}>
            <div style={{ ...gridItem, ...paginationContainer }}>
                {showColumnFilter && (
                    <ColumnFilter
                        columnDefs={columnDefs}
                        onColumnFilterChange={handleColumnFilterChange}
                        columnPannelWidth={190}
                        showApplyButton={showApplyButton}
                        onFilterClose={onFilterClose}
                    />
                )}
                {showPagination && (
                    <Pagination
                        pageInfo={pageInfo}
                        onPageSizeChange={onPageSizeChange}
                        onPrevClick={onPrevClick}
                        onNextClick={onNextClick}
                    />
                )}
            </div>
            {showDeleteBtns && (
                <div
                    style={{
                        ...buttonContainerStyle,
                        top: gridHeight + 5 + (containerRef?.current?.offsetTop ?? 0)
                    }}
                >
                    <AsButton
                        id='clear-all'
                        useSecondaryStyle
                        handleClick={handleClearSelectedDeletedRows}
                        style={buttonStyle}
                    >
                        {window.translate('Clear All')}
                    </AsButton>
                    <AsButton
                        id='delete-all'
                        handleClick={handleMultiRowDelete}
                        style={buttonStyle}
                    >
                        {window.translate('Delete Selected')}
                    </AsButton>
                </div>
            )}
            <ScrollableArea
                ref={containerRef}
                style={{
                    ...gridItem,
                    marginBottom: 10,
                    overflowX: 'hidden',
                    borderBottom: border,
                    ...scrollableAreaStyle
                }}
                onScroll={setScrollPxV}
                scrollPx={scrollPxV}
                height={gridHeight}
                maxVisibleLength={rowHeight * maxVisibleRows}
                width='100%'
                scrollableLength={rowHeight * data.length}
                scrollSpeed={16}
            >
                <table
                    {...props}
                    ref={tableRef}
                    style={{
                        ...gridItemTable,
                        ...style,
                        borderSpacing: 0,
                        ...(showDeleteBtns ? { userSelect: 'none' } : {})
                    }}
                >
                    <HeaderRow
                        definitions={columnDefsToDisplayInOrder()}
                        onSortOrderChange={
                            typeof onSortOrderChange === 'function'
                                ? handleSortOrderChange
                                : undefined
                        }
                        headerRowHeight={headerRowHeight}
                        scrollPxH={scrollPxH}
                        onScroll={setScrollPxH}
                        tableWidth={tableWidth}
                        tableOffsetH={tableOffsetH}
                        containerWidth={containerWidth}
                        addGutterCap
                        onTick={onTickAll}
                        areAllSelected={areAllTicked}
                        actionIconSize={actionIconSize}
                    />
                    <DataRows
                        definitions={rowDefinitions()}
                        rowIds={rowIds}
                        data={data}
                        maxVisibleRows={maxVisibleRows}
                        newlyCreated={newlyCreated}
                        onActionClick={onActionClick}
                        onFavouriteClick={onFavouriteClick}
                        primaryColumn={primaryColumn}
                        onPrimaryColumnClick={onPrimaryColumnClick}
                        onActionIconLoad={onActionIconLoad}
                        onActionToolTip={onActionToolTip}
                        onCustomClick={onCustomClick}
                        scrollPxH={scrollPxH}
                        scrollPxV={scrollPxV}
                        onScroll={setScrollPxV}
                        dateFormat={dateFormat}
                        timeFormat={timeFormat}
                        locale={locale}
                        currency={currency}
                        gutterOffset={tableOffsetV + headerRowHeight}
                        onKeyPress={onKeyPress}
                        rowHeight={rowHeight}
                        // Used by select/tickbox cells
                        onTick={onTick}
                        actionIconSize={actionIconSize}
                        selectedDeleteRows={selectedDeleteRows}
                        addToDelete={
                            typeof onMultiRowDelete === 'function'
                                ? handleAddToSelectedDeleteRows
                                : null
                        }
                        highlightOnClick={highlightOnClick}
                        onCustomCellStyle={onCustomCellStyle}
                    />
                </table>
            </ScrollableArea>
            {showPagination && (
                <div style={{ ...gridItem, ...paginationContainer }}>
                    <Pagination
                        pageInfo={pageInfo}
                        onPageSizeChange={onPageSizeChange}
                        invert
                        onPrevClick={onPrevClick}
                        onNextClick={onNextClick}
                    />
                </div>
            )}
            <div style={gridItem}>
                {/* bumber area to prevent the bottom part from shrinking */}
            </div>
        </div>
    );
};

AsGrid.propTypes = {
    columnDefs: PropTypes.arrayOf(PropTypes.object).isRequired,
    primaryColumn: PropTypes.string,
    headerRowHeight: PropTypes.any,
    maxVisibleRows: PropTypes.number,
    data: PropTypes.arrayOf(PropTypes.object),
    newlyCreated: PropTypes.object,
    pageInfo: PropTypes.object.isRequired,
    showColumnFilter: PropTypes.bool,
    onPageSizeChange: PropTypes.func,
    onNextClick: PropTypes.func,
    onPrevClick: PropTypes.func,
    onSortOrderChange: PropTypes.func,
    onActionClick: PropTypes.func,
    onFavouriteClick: PropTypes.func,
    onPrimaryColumnClick: PropTypes.func,
    onColumnFilterChange: PropTypes.func,
    onActionIconLoad: PropTypes.func,
    onActionToolTip: PropTypes.func,
    onCustomClick: PropTypes.func,
    onKeyPress: PropTypes.func,
    style: PropTypes.object,
    gridContainerStyle: PropTypes.object,
    dateFormat: PropTypes.string,
    timeFormat: PropTypes.string,
    locale: PropTypes.string,
    currency: PropTypes.string,
    maxHeight: PropTypes.any,
    onTick: PropTypes.func,
    onTickAll: PropTypes.func,
    areAllTicked: PropTypes.bool,
    showApplyButton: PropTypes.bool,
    onFilterClose: PropTypes.func,
    scrollableAreaStyle: PropTypes.object
};

export default AsGrid;
